import { Component, computed, CUSTOM_ELEMENTS_SCHEMA, inject, OnInit, signal } from '@angular/core';
import { Router } from '@angular/router';
import { StatusSolicitacao } from '../../../core/constants/status-solicitacao';
import { EnumStatusDocumento } from '../../../core/enum/EnumStatusDocumento';
import {
  Doc,
  DocumentoEnviado,
  DocumentosEnviadosResponse,
  DocumentoUpload,
  StatusAvaliacaoDocumento,
  TipoDocumento,
} from '../../../core/models/documento';
import { Steps } from '../../../shared/components/steps/steps';
import { ArquivoDocumento, UploadDocModal } from '../../../shared/components/upload-doc-modal/upload-doc-modal';
import { HttpClientService } from '../../../shared/services/http-client-service';
import { LoaderService } from '../../../shared/services/loader-service';
import { ToastService } from '../../../shared/services/toast-service';

/** Nome exibido no card de cada tipo de documento. */
const NOMES_DOCUMENTO: Record<TipoDocumento, string> = {
  CERTIDAO_NASCIMENTO: 'Certidão de nascimento',
  CNH_RG: 'CNH ou RG',
  COMPROVANTE_TITULARIDADE_CONTA: 'Documento comprobatório da conta',
};

/**
 * Status do card conforme a avaliação do documento enviado: pendente e aprovado ficam concluídos
 * (borda verde) e reprovado fica com erro (borda vermelha, reenvio liberado).
 */
const STATUS_POR_AVALIACAO: Record<StatusAvaliacaoDocumento, EnumStatusDocumento> = {
  PENDENTE: EnumStatusDocumento.Sucesso,
  APROVADO: EnumStatusDocumento.Sucesso,
  REPROVADO: EnumStatusDocumento.Erro,
};

/** Status em que o card fica travado: sucesso (borda verde) e em análise (borda amarela). */
const STATUS_TRAVADOS: readonly EnumStatusDocumento[] = [EnumStatusDocumento.Sucesso, EnumStatusDocumento.EmAnalise];

/** Documento exibido no card, já combinado com o retorno da API. */
interface DocumentoCard extends Doc {
  /** Já enviado, em análise ou aprovado: não permite novo upload. */
  travado: boolean;
  /**
   * Já enviado e ainda pendente, fora de AGUARDANDO_VALIDACAO_DOCUMENTOS: o arquivo enviado vale para
   * continuar, mas o participante pode trocá-lo por outro.
   */
  substituivel: boolean;
  /** Voltou com erro: borda vermelha, reenvio liberado e mensagem de reenvio na tela. */
  erro: boolean;
}

@Component({
  imports: [UploadDocModal, Steps],
  selector: 'app-upload-documentos',
  styleUrl: './upload-documentos.scss',
  templateUrl: './upload-documentos.html',
  schemas: [CUSTOM_ELEMENTS_SCHEMA],
})
export class UploadDocumentos implements OnInit {
  private readonly router = inject(Router);
  private readonly httpClientService = inject(HttpClientService);
  private readonly loaderService = inject(LoaderService);
  private readonly toastService = inject(ToastService);

  readonly stepAtual = signal(5);
  readonly totalSteps = signal(7);
  /** Evita mostrar os cards no estado inicial antes da resposta da API. */
  readonly carregado = signal(false);
  /** Modal de "documentos sendo enviados", aberto enquanto os documentos são enviados. */
  readonly enviando = signal(false);

  /** Retorno da API (GET ou do último envio); nulo enquanto ela não responde. */
  private readonly retorno = signal<DocumentosEnviadosResponse | null>(null);
  /** Arquivos anexados nos cards, ainda não enviados; só vão para a API no continuar. */
  readonly documentosAnexados = signal<DocumentoUpload[]>([]);

  /**
   * Um card para cada tipo obrigatório, na ordem da API (tipos desconhecidos são ignorados).
   * - Não está em `documentos`: card liberado para anexar o arquivo.
   * - Está em `documentos`: segue o status do documento (statusDoDocumento). Aprovado fica travado
   *   (borda verde); com erro, fica com borda vermelha e liberado para reenvio.
   * - Pendente: só fica travado, como concluído (borda verde), com a solicitação em
   *   AGUARDANDO_VALIDACAO_DOCUMENTOS; nos demais status fica liberado para troca, mostrando o arquivo enviado.
   */
  readonly documentos = computed<DocumentoCard[]>(() => {
    const { tiposObrigatorios = [], documentos = [], statusSolicitacao } = this.retorno() ?? {};
    const emValidacao = statusSolicitacao === StatusSolicitacao.AGUARDANDO_VALIDACAO_DOCUMENTOS;

    return tiposObrigatorios
      .filter((tipo) => tipo in NOMES_DOCUMENTO)
      .map((tipoDocumento) => {
        const enviado = documentos.find((doc) => doc.tipoDocumento === tipoDocumento);
        const idStatus = enviado ? this.statusDoDocumento(enviado) : undefined;
        const substituivel = !emValidacao && enviado?.statusAvaliacao === 'PENDENTE';
        const travado = !substituivel && idStatus !== undefined && STATUS_TRAVADOS.includes(idStatus);

        return {
          tipoDocumento,
          nomeDocumento: NOMES_DOCUMENTO[tipoDocumento],
          ...(travado && { idStatus, nomeArquivo: enviado!.nomeArquivo }),
          ...(substituivel && { nomeArquivo: enviado!.nomeArquivo }),
          travado,
          substituivel,
          erro: idStatus === EnumStatusDocumento.Erro,
        };
      });
  });

  /** O GET terminou (com sucesso ou erro); até lá o botão "Voltar" fica escondido. */
  private readonly buscaConcluida = signal(false);

  /**
   * O botão "Voltar" só aparece depois da resposta do GET e some quando a solicitação está aguardando
   * o reenvio de documentos. Se o GET falhar, aparece, para o participante não ficar sem saída.
   */
  readonly mostrarVoltar = computed(
    () => this.buscaConcluida() && this.retorno()?.statusSolicitacao !== StatusSolicitacao.AGUARDANDO_REENVIO,
  );

  /** Algum documento voltou com erro: mostra a mensagem de reenvio e o botão "Reenviar documentos". */
  readonly possuiErro = computed(() => this.documentos().some((doc) => doc.erro));

  /**
   * Libera o continuar quando todo documento obrigatório já foi enviado ou tem arquivo anexado.
   * Exige que a API tenha respondido: sem os cards (ex.: erro ao buscar), a lista vazia liberaria o botão.
   */
  readonly podeContinuar = computed(
    () =>
      this.carregado() &&
      !this.enviando() &&
      this.documentos().every((doc) => doc.travado || doc.substituivel || this.anexado(doc.tipoDocumento)),
  );

  ngOnInit(): void {
    void this.carregarDocumentosEnviados();
  }

  /** Busca os tipos de documento obrigatórios e os que o participante já enviou. */
  protected async carregarDocumentosEnviados(): Promise<void> {
    this.loaderService.showLoader('Carregando...');

    try {
      const { body } = await this.httpClientService.getDocumentosEnviados();
      this.retorno.set(body);
      this.carregado.set(true);
    } catch {
      this.toastService.show('Não foi possível verificar os documentos. Tente novamente.', 'error');
    } finally {
      this.buscaConcluida.set(true);
      this.loaderService.hideLoader();
    }
  }

  /** Guarda (ou remove, com `file` nulo) o arquivo anexado no card; o envio só acontece no continuar. */
  onArquivoDocumento({ tipoDocumento, file }: ArquivoDocumento): void {
    this.documentosAnexados.update((anexados) => [
      ...anexados.filter((item) => item.tipoDocumento !== tipoDocumento),
      ...(file ? [{ tipoDocumento, arquivo: file }] : []),
    ]);
  }

  closeModal(): void {
    this.enviando.set(false);
  }

  /** Arquivo anexado no card do tipo informado, ainda não enviado. */
  private anexado(tipoDocumento: string): DocumentoUpload | undefined {
    return this.documentosAnexados().find((item) => item.tipoDocumento === tipoDocumento);
  }

  /**
   * Status de um documento já enviado, conforme o `statusAvaliacao` da API. Um valor desconhecido
   * fica em análise, travado, para não liberar reenvio nem mostrar como aprovado por engano.
   */
  protected statusDoDocumento({ statusAvaliacao }: DocumentoEnviado): EnumStatusDocumento {
    return STATUS_POR_AVALIACAO[statusAvaliacao] ?? EnumStatusDocumento.EmAnalise;
  }

  onReturn(): void {
    this.router.navigate(['/instituto-online/modalidade-ir']);
  }

  /**
   * Envia os documentos anexados para a API, um por vez, na ordem dos cards, e segue para o resumo
   * quando o último envio retorna AGUARDANDO_REVISAO. Cada resposta atualiza os cards;
   * se um envio falhar, para ali: os já enviados ficam travados e o restante continua anexado, para
   * o participante tentar de novo. Documento substituível sem arquivo novo não é reenviado.
   */
  async onSubmit(): Promise<void> {
    if (!this.podeContinuar()) return;

    const pendentes = this.documentos()
      .filter((doc) => !doc.travado)
      .map((doc) => this.anexado(doc.tipoDocumento))
      .filter((documento): documento is DocumentoUpload => documento !== undefined);

    if (pendentes.length === 0) {
      this.router.navigate(['/instituto-online/resumo']);
      return;
    }

    this.enviando.set(true);

    try {
      for (const documento of pendentes) {
        const { body } = await this.httpClientService.saveDocumento(documento);
        this.retorno.set(body);
        this.documentosAnexados.update((anexados) =>
          anexados.filter((item) => item.tipoDocumento !== documento.tipoDocumento),
        );
      }
    } catch {
      this.toastService.show('Não foi possível enviar os documentos. Tente novamente.', 'error');
      return;
    } finally {
      this.enviando.set(false);
    }

    // A API devolve AGUARDANDO_REVISAO só depois do envio do último documento obrigatório.
    if (this.retorno()?.statusSolicitacao === StatusSolicitacao.AGUARDANDO_REVISAO) {
      this.router.navigate(['/instituto-online/resumo']);
    } else if (this.retorno()?.statusSolicitacao === StatusSolicitacao.AGUARDANDO_VALIDACAO_DOCUMENTOS) {
      this.router.navigate(['/instituto-online/detalhes']);
    } else {
      this.toastService.show('Não foi possível confirmar o envio dos documentos. Tente novamente.', 'error');
    }
  }
}
